import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { get } from 'lodash-es';
import { Trigger } from '../models/client-settings';
import { IncomingCallInfo } from '../models/incoming-call-info';
import { TriggerResponseInfo } from '../models/trigger-response-info';
import { Utilities } from '../utils/utilities';
import { AuthService } from './auth.service';
import { LoggingService } from './logging.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root',
})
export class PlaceholderService {
  public constructor(
    private readonly settingsService: SettingsService,
    private readonly authService: AuthService,
    private readonly loggingService: LoggingService,
  ) {}

  public applyPlaceholders(trigger: Trigger, callInfo: IncomingCallInfo | null = null, triggerResponseInfo: TriggerResponseInfo): void {
    this.applySystemPlaceholders(trigger, callInfo);
    this.applyResponsePlaceholders(trigger, triggerResponseInfo);
    this.loggingReplacedTriggerDetails(trigger);
  }

  private loggingReplacedTriggerDetails(trigger: Trigger): void {
    if (trigger.url) {
      this.loggingService.Info(`Trigger url: ${trigger.url}`);
    }
    if (trigger.body) {
      this.loggingService.Info(`Trigger body: ${trigger.body}`);
    }
    if (trigger.command?.path && trigger.command?.arguments) {
      this.loggingService.Info(`Trigger command: ${trigger.command.path} ${trigger.command.arguments}`);
    }
    if (trigger.multipartFormData?.length) {
      this.loggingService.Info(`Trigger multipartFormData: ${trigger.multipartFormData.map((x) => `${x.key}: ${x.value};`).join(' ')}`);
    }
    if (trigger.headers?.length) {
      this.loggingService.Info(`Trigger headers: ${trigger.headers.map((x) => `${x.key}: ${x.value};`).join(' ')}`);
    }
  }

  private applySystemPlaceholders(trigger: Trigger, callInfo: IncomingCallInfo | null = null): void {
    const placeholders = this.settingsService.settings$.value?.placeholders;
    const parametersRegexp = /\$\([^\)]+\)/;
    if (!placeholders || !trigger) {
      return;
    }

    placeholders.forEach((x) => {
      if (this.triggerHasPlaceholder(trigger, x)) {
        const value = this.getPlaceholderValue(x, callInfo);
        this.replacePlaceholderInTrigger(trigger, x, value);
      }
    });

    if (callInfo?.isServiceCall && callInfo?.resolvedParameters) {
      Object.keys(callInfo.resolvedParameters).forEach((key) => {
        if (this.triggerHasPlaceholder(trigger, key)) {
          this.replacePlaceholderInTrigger(trigger, key, callInfo.resolvedParameters[key]);
        }
      });
    }

    if (trigger?.multipartFormData?.length > 0) {
      trigger.multipartFormData.forEach((param) => {
        if (parametersRegexp.test(param.value)) {
          param.value = this.replaceParameters(param.value, callInfo);
        }
      });
    }
  }

  private getPlaceholderValue(placeholder: string, callInfo: IncomingCallInfo): string {
    let value = '';
    const profile = this.authService.profile$.getValue();
    switch (placeholder) {
      case '$(Caller.+TelNumber)':
        if (callInfo?.phoneNumber) {
          value = callInfo.phoneNumber.startsWith('+') ? callInfo.phoneNumber : `+${callInfo.phoneNumber}`;
        }
        break;
      case '$(Caller.TelNumber)':
        if (callInfo?.phoneNumber) {
          value = callInfo.phoneNumber.replace('+', '');
        }
        break;
      case '$(Caller.DisplayName)':
        if (callInfo?.callerDisplayName?.replace(/ /g, '')) {
          value = callInfo.callerDisplayName;
        }
        break;
      case '$(Caller.Id)':
        if (callInfo?.microsoftTeamsUserId) {
          value = callInfo.microsoftTeamsUserId;
        }
        break;
      case '$(User.Id)':
        const o365Id = this.settingsService.settings$.getValue().office365Id;
        if (o365Id) {
          value = o365Id;
        }
        break;
      case '$(User.Email)':
        if (profile && profile.mail) {
          value = profile.mail;
        }
        break;
      case '$(User.DisplayName)':
        if (profile && profile.displayName) {
          value = profile.displayName;
        }
        break;
      case '$(User.Upn)':
        if (profile && profile.userPrincipalName) {
          value = profile.userPrincipalName;
        }
        break;
      case '$(Random.Guid)':
        value = uuid();
        break;
      default:
        this.loggingService.Error(`Placeholder was not replaced: ${placeholder}`);
    }
    return value;
  }

  private applyResponsePlaceholders(trigger: Trigger, triggerResponseInfo: TriggerResponseInfo): void {
    Object.keys(triggerResponseInfo).forEach((key) => this.applyTriggerPlaceholders(trigger, key, triggerResponseInfo));
  }

  private applyTriggerPlaceholders(trigger: Trigger, key: string, triggerResponseInfo: TriggerResponseInfo): void {
    const bodyPlaceholder = `{{${key}.body}}`;
    const regex = new RegExp(`{{${key}\.[^}]*}}`, 'g');
    const triggerFields = `${trigger.url || ''}${trigger.body || ''}${trigger.command?.arguments || ''}${trigger.headers
      ?.map((x) => x.value)
      .join(' ')}`;
    const matches = Array.from(new Set(triggerFields.match(regex)));

    matches.forEach((placeholder) => {
      const path = placeholder.replace(/{|}/g, '');
      let value = get(triggerResponseInfo, path);
      if (placeholder === bodyPlaceholder) {
        value = JSON.stringify(value);
      }

      this.replacePlaceholderInTrigger(trigger, placeholder, value);
    });
  }

  private replaceParameters(x: string, callInfo: IncomingCallInfo): string {
    if (callInfo?.isServiceCall) {
      if (callInfo.resolvedParameters?.hasOwnProperty(x)) {
        return callInfo.resolvedParameters[x] || '';
      }
    }
    return this.getPlaceholderValue(x, callInfo);
  }

  private triggerHasPlaceholder(trigger: Trigger, placeholder: string): boolean {
    if (!trigger) {
      return false;
    }

    return (
      trigger.url?.includes(placeholder) ||
      trigger.body?.includes(placeholder) ||
      trigger.command?.arguments?.includes(placeholder) ||
      trigger.headers
        ?.map((x) => x.value)
        .join(' ')
        .includes(placeholder)
    );
  }

  private replacePlaceholderInTrigger(trigger: Trigger, placeholder: string, value: string): void {
    const valueEncoded = encodeURI(value);
    const encodeBody = trigger.headers.find((x) => x.key.toLowerCase() === 'content-type' && x.value.toLowerCase().includes('urlencoded'));

    // When the trigger opens in a browser, a literal "+" in the query string is decoded as a space.
    // Percent-encode it so values such as $(Caller.+TelNumber) keep their leading "+".
    const urlValueEncoded = trigger.openInBrowser ? valueEncoded.replace(/\+/g, '%2B') : valueEncoded;

    trigger.url = Utilities.replaceAll(trigger.url, placeholder, urlValueEncoded);
    trigger.body = Utilities.replaceAll(trigger.body, placeholder, encodeBody ? valueEncoded : value);
    if (trigger.command?.arguments) {
      trigger.command.arguments = Utilities.replaceAll(trigger.command.arguments, placeholder, value);
    }

    trigger.headers = (trigger.headers || []).map((x) => {
      x.value = Utilities.replaceAll(x.value, placeholder, value);
      return x;
    });
  }
}
